import React, { useState } from "react";
import { useApi } from "../../hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
    Sparkles,
    Send,
    FileText,
    AlertTriangle,
    BarChart3,
    Loader2,
    Copy,
    Check,
} from "lucide-react";
import { cn } from "../../lib/utils";

export const AIAssistantPage = () => {
    const { post } = useApi();
    const [prompt, setPrompt] = useState("");
    const [type, setType] = useState("general");
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState(null);
    const [copied, setCopied] = useState(false);
    const [history, setHistory] = useState([]);

    const typeOptions = [
        { value: "general", label: "Général", icon: Sparkles, description: "Questions générales sur vos projets" },
        { value: "summary", label: "Résumé", icon: FileText, description: "Générer des résumés de données" },
        { value: "report", label: "Rapport", icon: BarChart3, description: "Créer des rapports structurés" },
        { value: "risk_analysis", label: "Analyse de risques", icon: AlertTriangle, description: "Identifier et analyser les risques" },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!prompt.trim()) {
            toast.error("Veuillez entrer une question ou une demande");
            return;
        }

        setLoading(true);
        setResponse(null);

        try {
            const result = await post("/ai/generate", { prompt, type });
            setResponse(result.response);
            setHistory((prev) => [
                { prompt, type, response: result.response, timestamp: new Date() },
                ...prev.slice(0, 9),
            ]);
        } catch (error) {
            toast.error("Erreur lors de la génération de la réponse IA");
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async () => {
        if (!response) return;
        try {
            await navigator.clipboard.writeText(response);
            setCopied(true);
            toast.success("Copié dans le presse-papiers");
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            toast.error("Erreur lors de la copie");
        }
    };

    const quickPrompts = [
        "Résume l'état actuel de tous mes projets en cours",
        "Quels sont les risques potentiels sur mes projets ?",
        "Génère un rapport mensuel de mes activités",
        "Analyse la charge de travail de mon équipe",
        "Quelles sont les factures en retard et les actions recommandées ?",
    ];

    return (
        <div className="space-y-6 animate-fade-in" data-testid="ai-assistant-page">
            {/* Header */}
            <div>
                <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    Assistant IA
                </h1>
                <p className="text-muted-foreground mt-2">
                    Utilisez l'intelligence artificielle pour analyser vos données et générer des insights
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Main Input Area */}
                <div className="lg:col-span-8 space-y-6">
                    {/* Type Selection */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {typeOptions.map((option) => {
                            const Icon = option.icon;
                            return (
                                <button
                                    key={option.value}
                                    onClick={() => setType(option.value)}
                                    className={cn(
                                        "p-4 rounded-xl border text-left transition-all duration-200",
                                        type === option.value
                                            ? "border-primary bg-primary/5 shadow-sm"
                                            : "border-border hover:border-primary/50 hover:bg-secondary/50"
                                    )}
                                    data-testid={`ai-type-${option.value}`}
                                >
                                    <Icon
                                        className={cn(
                                            "w-5 h-5 mb-2",
                                            type === option.value ? "text-primary" : "text-muted-foreground"
                                        )}
                                    />
                                    <p className="font-medium text-sm">{option.label}</p>
                                    <p className="text-xs text-muted-foreground mt-1">{option.description}</p>
                                </button>
                            );
                        })}
                    </div>

                    {/* Input Form */}
                    <Card>
                        <CardContent className="p-6">
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <Textarea
                                    placeholder="Posez votre question ou décrivez ce que vous souhaitez générer..."
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    rows={4}
                                    className="resize-none"
                                    data-testid="ai-prompt-input"
                                />
                                <div className="flex justify-end">
                                    <Button type="submit" disabled={loading || !prompt.trim()} data-testid="ai-submit-btn">
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Génération...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="w-4 h-4 mr-2" />
                                                Générer
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>

                    {/* Response */}
                    {response && (
                        <Card className="animate-slide-up" data-testid="ai-response-card">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="font-heading text-lg flex items-center gap-2">
                                    <Sparkles className="w-5 h-5 text-primary" />
                                    Réponse IA
                                </CardTitle>
                                <Button variant="ghost" size="sm" onClick={copyToClipboard}>
                                    {copied ? (
                                        <Check className="w-4 h-4 text-green-600" />
                                    ) : (
                                        <Copy className="w-4 h-4" />
                                    )}
                                </Button>
                            </CardHeader>
                            <CardContent>
                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                    <div className="whitespace-pre-wrap text-sm leading-relaxed" data-testid="ai-response-text">
                                        {response}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Sidebar */}
                <div className="lg:col-span-4 space-y-6">
                    {/* Quick Prompts */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading text-lg">Suggestions rapides</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            {quickPrompts.map((qPrompt, index) => (
                                <button
                                    key={index}
                                    onClick={() => setPrompt(qPrompt)}
                                    className="w-full text-left p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-secondary/50 transition-all duration-200 text-sm"
                                    data-testid={`quick-prompt-${index}`}
                                >
                                    {qPrompt}
                                </button>
                            ))}
                        </CardContent>
                    </Card>

                    {/* History */}
                    {history.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading text-lg">Historique récent</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {history.slice(0, 5).map((item, index) => (
                                    <div
                                        key={index}
                                        className="p-3 rounded-lg bg-secondary/50 cursor-pointer hover:bg-secondary transition-colors"
                                        onClick={() => {
                                            setPrompt(item.prompt);
                                            setType(item.type);
                                            setResponse(item.response);
                                        }}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <Badge variant="outline" className="text-xs">
                                                {typeOptions.find((t) => t.value === item.type)?.label}
                                            </Badge>
                                        </div>
                                        <p className="text-sm text-muted-foreground line-clamp-2">{item.prompt}</p>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
};
